import { useState, useEffect } from 'preact/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckSquare, Plus, Trash2, Edit2, Check } from 'lucide-preact';

interface TodoProps {
  projectId: string;
  projectName: string;
}

interface Todo {
  id: string;
  text: string;
  completed: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export function Todos({ projectId, projectName }: TodoProps) {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [newTodo, setNewTodo] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  // Load todos from localStorage
  useEffect(() => {
    const savedTodos = localStorage.getItem(`todos-${projectId}`);
    if (savedTodos) {
      try {
        const parsedTodos = JSON.parse(savedTodos).map((todo: any) => ({
          ...todo,
          createdAt: new Date(todo.createdAt),
          updatedAt: new Date(todo.updatedAt)
        }));
        setTodos(parsedTodos);
      } catch (error) {
        console.error('Failed to parse saved todos:', error);
      }
    }
  }, [projectId]);

  // Save todos to localStorage
  useEffect(() => {
    if (todos.length > 0) {
      localStorage.setItem(`todos-${projectId}`, JSON.stringify(todos));
    }
  }, [todos, projectId]);

  const addTodo = () => {
    if (!newTodo.trim()) return;

    const todo: Todo = {
      id: Date.now().toString(),
      text: newTodo.trim(),
      completed: false,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setTodos(prev => [...prev, todo]);
    setNewTodo('');
  };

  const toggleTodo = (id: string) => {
    setTodos(prev => prev.map(todo => 
      todo.id === id 
        ? { ...todo, completed: !todo.completed, updatedAt: new Date() }
        : todo
    ));
  };

  const deleteTodo = (id: string) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  };

  const startEditing = (id: string, text: string) => {
    setEditingId(id);
    setEditingText(text);
  };

  const saveEdit = () => {
    if (!editingText.trim()) return;

    setTodos(prev => prev.map(todo => 
      todo.id === editingId 
        ? { ...todo, text: editingText.trim(), updatedAt: new Date() }
        : todo
    ));
    setEditingId(null);
    setEditingText('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingId) {
        saveEdit();
      } else {
        addTodo();
      }
    } else if (e.key === 'Escape' && editingId) {
      cancelEdit();
    }
  };

  const completedCount = todos.filter(todo => todo.completed).length;
  const totalCount = todos.length;

  return (
    <div className="flex flex-col h-full">
      <Card className="flex-1 flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            Todos - {projectName}
          </CardTitle>
          {totalCount > 0 && (
            <div className="text-sm text-muted-foreground">
              {completedCount} of {totalCount} completed
            </div>
          )}
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-0">
          {/* Add new todo */}
          <div className="p-4 border-b">
            <div className="flex gap-2">
              <Input
                value={newTodo}
                onChange={(e) => setNewTodo((e.target as HTMLInputElement).value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a new todo..."
                className="flex-1"
              />
              <Button onClick={addTodo} disabled={!newTodo.trim()} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Todo list */}
          <div className="flex-1 overflow-y-auto p-4">
            {todos.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <CheckSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No todos yet. Add one above to get started!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {todos.map((todo) => (
                  <div
                    key={todo.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      todo.completed ? 'bg-muted/50' : 'bg-background'
                    }`}
                  >
                    <button
                      onClick={() => toggleTodo(todo.id)}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                        todo.completed
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-muted-foreground hover:border-primary'
                      }`}
                    >
                      {todo.completed && <Check className="h-3 w-3" />}
                    </button>
                    
                    {editingId === todo.id ? (
                      <div className="flex-1 flex gap-2">
                        <Input
                          value={editingText}
                          onChange={(e) => setEditingText((e.target as HTMLInputElement).value)}
                          onKeyPress={handleKeyPress}
                          className="flex-1"
                          autoFocus
                        />
                        <Button onClick={saveEdit} size="sm" variant="outline">
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button onClick={cancelEdit} size="sm" variant="outline">
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span
                          className={`flex-1 text-sm ${
                            todo.completed ? 'line-through text-muted-foreground' : ''
                          }`}
                        >
                          {todo.text}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            onClick={() => startEditing(todo.id, todo.text)}
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            onClick={() => deleteTodo(todo.id)}
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
